import '../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import pinGuide from './assets/pin-extension-guide-logo.png';
import saveModes from './assets/save-modes-reordered.png';

const ExtensionOnboarding = () => {
    const {t: lnT} = useTranslation();

    const [showGuide, setShowGuide] = useState(true);

    return (
        <PageShell>
            <Helmet>
                <title>{lnT('onboarding:setUpTheLuckynoteExtension')}</title>
                <meta name="description" content="Pin the Luckynote extension and save anything you find online." />
            </Helmet>

            <main>
                <section className="modes" aria-labelledby="save-anything">
                    <div className="section-copy">
                        <h2 id="save-anything">{lnT('onboarding:saveTheThingsWorthKeeping')}</h2>
                        <p>{lnT('onboarding:highlightsPagesImagesAndMediaAllLand')}</p>
                    </div>
                    <img src={saveModes} alt={lnT('onboarding:threeWaysToSaveTextWebPages')} />
                    <div className="mode-labels" aria-label={lnT('onboarding:waysToSave')}>
                        <span>{lnT('onboarding:imagesMedia')}</span>
                        <span>{lnT('onboarding:textQuotes')}</span>
                        <span>{lnT('onboarding:websitesArticles')}</span>
                    </div>
                </section>

                <section className="hero">
                    <h1>{lnT('onboarding:oneClickAndItS')}<br />{lnT('onboarding:savedToLuckynote')}</h1>
                    <p className="intro">{lnT('onboarding:keepTheUsefulThingsYouFindOnline')}</p>
                    <button className="primary" type="button" onClick={() => setShowGuide(true)}>{lnT('onboarding:pinTheExtension')}</button>
                </section>
            </main>

            {showGuide && (
                <div className="overlay" role="presentation">
                    <section className="guide" role="dialog" aria-modal="true" aria-labelledby="guide-title">
                        <button className="close" type="button" aria-label={lnT('onboarding:closeSetupGuide')} onClick={() => setShowGuide(false)}>×</button>
                        <p className="eyebrow">{lnT('onboarding:nextStep')}</p>
                        <h2 id="guide-title">{lnT('onboarding:pinTheExtension')}<br />{lnT('onboarding:toStartSaving')}</h2>
                        <img src={pinGuide} alt={lnT('onboarding:browserExtensionMenuShowingThePinControl')} />
                        <button className="ready" type="button" onClick={() => setShowGuide(false)}>{lnT('onboarding:startSaving')}</button>
                    </section>
                </div>
            )}
        </PageShell>
    );
};

const PageShell = styled.div`
    min-height: 100vh;
    overflow: hidden;
    color: #f8fbff;
    background:
        radial-gradient(circle at 9% 76%, rgba(255, 255, 255, .68) 0 2.5%, transparent 12%),
        radial-gradient(ellipse at 84% 62%, rgba(212, 246, 255, .55) 0 4%, transparent 18%),
        linear-gradient(180deg, #3778ae 0%, #62c8e3 100%);
    font-family: Arial, Helvetica, sans-serif;

    .hero { box-sizing: border-box; min-height: 78dvh; padding: 7rem 24px 4rem; background: transparent !important; text-align: center; }
    .brand, .eyebrow { margin: 0; color: #dceeff; font-size: .75rem; font-weight: 800; letter-spacing: .22em; }
    h1, h2 { font-family: Georgia, 'Times New Roman', serif; font-weight: 500; }
    .hero h1, .modes h2 { color: #f8fbff !important; }
    h1 { max-width: 650px; margin: 1.5rem auto 1rem; font-size: clamp(2.5rem, 5.2vw, 4.8rem); line-height: 1.02; letter-spacing: -.045em; }
    .intro { max-width: 330px; margin: 0 auto 1.5rem; font-size: .95rem; line-height: 1.5; color: rgba(248, 251, 255, .86); }
    button { font: inherit; cursor: pointer; }
    .primary, .ready { border: 2px solid currentColor; border-radius: 999px; padding: .9rem 1.6rem; color: #fff; background: transparent; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; transition: transform .2s ease, background .2s ease; }
    .primary:hover, .ready:hover { transform: translateY(-2px); background: rgba(255,255,255,.14); }
    .primary:active, .ready:active { transform: scale(.98); }

    .modes { max-width: 980px; margin: 0 auto; padding: 6rem 24px 3rem; background: transparent !important; text-align: center; }
    .section-copy { max-width: 490px; margin: 0 auto 1.8rem; }
    .section-copy h2 { margin: 0 0 .5rem; font-size: clamp(2rem, 3.8vw, 3.25rem); line-height: 1.05; letter-spacing: -.035em; }
    .section-copy p { margin: 0; color: rgba(248, 251, 255, .86); font-size: .95rem; line-height: 1.5; }
    .modes img { display: block; width: 100%; border-radius: 26px; box-shadow: 0 22px 60px rgba(19, 71, 106, .28); }
    .mode-labels { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-top: 1rem; font-family: Georgia, 'Times New Roman', serif; font-size: 1.05rem; }

    .overlay { position: fixed; z-index: 10; inset: 0; display: grid; justify-items: end; align-items: start; padding: 16px 4vw 28px 28px; background: rgba(14, 54, 82, .55); backdrop-filter: blur(14px); }
    .guide { position: relative; box-sizing: border-box; width: min(660px, 100%); padding: 2.6rem clamp(1.25rem, 4vw, 3rem) 1.8rem; border-radius: 24px; color: #222c3a; background: #fcfdff; text-align: center; box-shadow: 0 26px 75px rgba(7, 28, 48, .3); animation: guide-enter .45s cubic-bezier(.16, 1, .3, 1); }
    .guide:before { content: ''; position: absolute; top: -10px; right: 6%; width: 20px; height: 20px; background: #fcfdff; transform: rotate(45deg); }
    .guide .eyebrow { color: #4d7fc1; }
    .guide h2 { margin: .7rem auto 1.25rem; font-size: clamp(2rem, 4vw, 3.15rem); line-height: 1.06; letter-spacing: -.04em; }
    .guide img { display: block; width: 82%; max-height: 210px; margin: 0 auto; object-fit: cover; object-position: center; border-radius: 15px; box-shadow: 0 12px 28px rgba(41, 78, 111, .16); }
    .ready { margin-top: 1.3rem; color: #4d7fc1; }
    .close { position: absolute; z-index: 1; top: 18px; right: 18px; width: 42px; height: 42px; border: 0; border-radius: 50%; color: #fff; background: #9ba0a7; font-size: 2rem; font-weight: 200; line-height: 1; }
    .close:hover { background: #737b84; }

    @keyframes guide-enter { from { opacity: 0; transform: translateY(18px) scale(.98); } to { opacity: 1; transform: none; } }
    @media (prefers-reduced-motion: reduce) { *, *:before { animation: none !important; transition: none !important; } }
    @media (max-width: 640px) {
        .overlay { place-items: center; padding: 20px; }
        .hero { min-height: 70dvh; padding-top: 4rem; }
        h1 { font-size: clamp(2.4rem, 12vw, 3.3rem); }
        .modes { margin-top: -1rem; padding-bottom: 4rem; }
        .mode-labels { grid-template-columns: 1fr; gap: .4rem; font-size: 1.12rem; }
        .guide { padding: 3rem 1.25rem 1.5rem; border-radius: 23px; }
        .guide img { width: 90%; max-height: 170px; border-radius: 13px; }
        .guide h2 { font-size: 2.1rem; }
    }
`;

export default ExtensionOnboarding;
